import OnlyHeader from "components/Layout/OnlyHeader";
import React, { useEffect, useRef, useState } from "react";
import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Popover,
  PopoverBody,
  Row,
  Spinner,
} from "reactstrap";
import ItemModal from "./ItemModal";
import { useDispatch, useSelector } from "react-redux";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import {
  fetchItems,
  deleteItem,
  toggleItemVisibility,
} from "store/actions/itemsActions";
import { duplicateItem } from "store/actions/itemsActions";
import { toast } from "react-toastify";
import usePermissions from "hooks/usePermissions";
import { fetchAllLabels } from "store/actions/labelActions";

const Items = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { id: categoryId } = useParams();
  const { items } = useSelector((state) => state.item);

  const popoverRef = useRef(null);
  const {
    add: addAccess,
    edit: editAccess,
    delete: deleteAccess,
    visibility: visibilityAccess,
    duplicate: duplicateAccess,
  } = usePermissions();
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const menuParamId = pathSegments[3];
  const [filteredItems, setFilteredItems] = useState(items);
  const [search, setSearch] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [itemModal, setItemModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [visibilityModal, setVisibilityModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(null);
  const [isFetchingLabels, setIsFetchingLabels] = useState(false);
  const hasItemsAccess =
    duplicateAccess || visibilityAccess || editAccess || deleteAccess;
  const togglePopover = (id) => setPopoverOpen(popoverOpen === id ? null : id);
  const { allLabels } = useSelector((state) => state.label);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearch(query);

    const filtered = items.filter((item) =>
      item.name.toLowerCase().includes(query)
    );
    setFilteredItems(filtered);
  };

  const itemModalToggler = () => {
    if (itemModal) {
      setActiveItem(null);
      setEditMode(false);
      setViewMode(false);
    }
    setItemModal(!itemModal);
  };

  const duplicateModalToggler = () => {
    if (duplicateModal) {
      setActiveItem(null);
    }
    setDuplicateModal(!duplicateModal);
  };

  const visibilityModalToggler = () => {
    if (visibilityModal) {
      setActiveItem(null);
    }
    setVisibilityModal((prev) => !prev);
  };

  const confirmationModalToggler = () => {
    if (confirmationModal) {
      setActiveItem(null);
    }
    setConfirmationModal(!confirmationModal);
  };

  const initiateViewItem = (item) => {
    setPopoverOpen(null);
    setActiveItem(item);
    setViewMode(true);
    itemModalToggler();
  };

  const initiateUpdateItem = (item) => {
    setPopoverOpen(null);
    setActiveItem(item);
    setEditMode(true);
    itemModalToggler();
  };

  const initiateDuplicate = (item) => {
    setPopoverOpen(null);
    setActiveItem(item);
    duplicateModalToggler();
  };

  const initiateToggleVisibility = (e, item) => {
    setPopoverOpen(null);
    setActiveItem(item);
    visibilityModalToggler();
  };

  const initiateDelete = (item) => {
    setPopoverOpen(null);
    setActiveItem(item);
    confirmationModalToggler();
  };

  const handleDuplicateItem = () => {
    setIsLoading(true);
    const { id, ...restItemData } = activeItem;
    dispatch(duplicateItem({ categoryId, itemData: restItemData }))
      .then(() => {
        toast.success("Item Duplicated Successfully");
        duplicateModalToggler();
      })
      .finally(() => setIsLoading(false));
  };

  const handleDeleteItem = () => {
    setIsLoading(true);
    dispatch(deleteItem(activeItem?.id, activeItem?.images))
      .then(() => {
        toast.success("Item Deleted Successfully");
        confirmationModalToggler();
      })
      .finally(() => setIsLoading(false));
  };

  const handleToggleItemVisibility = async () => {
    setIsLoading(true);
    dispatch(toggleItemVisibility(activeItem?.id, !activeItem?.isVisible))
      .then(() => visibilityModalToggler())
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (user?.restaurantID && categoryId) {
      setIsFetching(true);
      dispatch(
        fetchItems({ restaurantID: user?.restaurantID, categoryId })
      ).finally(() => setIsFetching(false));
    }
  }, [user, categoryId, dispatch]);

  useEffect(() => {
    setIsFetchingLabels(true);
    dispatch(fetchAllLabels(user?.restaurantID || "")).finally(() =>
      setIsFetchingLabels(false)
    );
  }, [dispatch, user?.restaurantID]);

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !event.target.closest(".popover")
      ) {
        setPopoverOpen(null);
      }
    };

    if (popoverOpen !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popoverOpen]);

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center px-2 px-lg-4">
                <Col xs="12" lg="3" xl="6" className="mb-3 mb-lg-0">
                  <div className="d-flex align-items-center">
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => history.goBack()}
                    >
                      <i className="fas fa-arrow-left " />
                    </Button>

                    <h3 className="mb-0">Items</h3>
                  </div>
                </Col>
                <Row className="w-100 mx-0 justify-content-end">
                  <Col xs="12" sm="8" lg="8" className="mb-3 mb-sm-0">
                    <Input
                      placeholder="Search by item name"
                      value={search}
                      onChange={handleSearch}
                    />
                  </Col>
                  {addAccess && (
                    <Col xs="12" sm="4" lg="4">
                      <Button
                        className="w-100"
                        color="primary"
                        onClick={itemModalToggler}
                      >
                        Add
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardHeader>

              <CardBody>
                {isFetching ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : filteredItems?.length ? (
                  <Row className="p-0 p-lg-3">
                    {filteredItems?.map((item, index) => (
                      <Col
                        key={item?.id}
                        xl={4}
                        lg={4}
                        md={6}
                        sm={6}
                        xs={12}
                        className="mb-4"
                      >
                        <Card className="w-100 h-100 shadow-sm overflow-hidden border border-gray">
                          <CardHeader
                            className="relative w-100 p-0 overflow-hidden"
                            style={{ height: "175px" }}
                          >
                            {!item?.isVisible && (
                              <Badge
                                color="danger"
                                className="position-absolute top-2 left-2"
                              >
                                Hidden
                              </Badge>
                            )}
                            <img
                              src={
                                item?.images?.length
                                  ? item?.images[0]
                                  : "https://res.cloudinary.com/dzhvjtoot/image/upload/v1712309249/logo/ogb3puh0buzqtzem3ce1.png"
                              }
                              className="w-100 h-100"
                              style={{ objectFit: "cover" }}
                              alt="item img"
                            />
                            <div ref={popoverRef}></div>
                          </CardHeader>
                          <CardBody className="px-3 py-2 bg-secondary d-flex flex-column justify-content-between">
                            <div>
                              <CardTitle tag="h3" className="mb-0 p-0">
                                {item?.name}
                              </CardTitle>
                              <div
                                style={{ fontSize: "12px" }}
                                className="mt-1"
                              >
                                <div>
                                  <i className="fa fa-clock mr-1" />
                                  {item?.estimatedTime} (mins)
                                </div>
                                {item?.addOns?.length > 0 && (
                                  <div>
                                    <i className="fa-solid fa-plus mr-1" />
                                    {item.addOns
                                      .map((addOn) => addOn.name)
                                      .join(", ")}
                                  </div>
                                )}
                                <div className="d-flex mt-2">
                                  {item.labels && item.labels.length > 0
                                    ? item.labels.map((lbl) => {
                                        const labelData = allLabels.find(
                                          (label) =>
                                            label.labelName === lbl.label
                                        );
                                        return (
                                          labelData && (
                                            <img
                                              key={labelData.labelName}
                                              src={labelData.labelURL}
                                              alt={labelData.labelName}
                                              title={labelData.labelName}
                                              style={{
                                                width: 25,
                                                height: 25,
                                                marginRight: 10,
                                                objectFit: "contain",
                                              }}
                                            />
                                          )
                                        );
                                      })
                                    : null}
                                </div>
                              </div>
                            </div>
                            <div className="d-flex justify-content-end align-items-center">
                              <span style={{ fontSize: "12px" }}>
                                Starting from:
                              </span>
                              <h2 className="ml-1 mb-0">
                                {item?.variants[0]?.price}
                                {user?.currency || "$"}
                              </h2>
                            </div>
                          </CardBody>
                          <CardFooter className="p-0 bg-secondary d-flex">
                            {/* Left 75% - View Items */}
                            <div
                              className={`d-flex align-items-center ${
                                hasItemsAccess ? "w-75" : "w-100"
                              }  p-2`}
                            >
                              <Button
                                color="success"
                                className="w-100 d-flex align-items-center justify-content-center"
                                size="sm"
                                onClick={() => initiateViewItem(item)}
                              >
                                <i className="fa-solid fa-circle-info mr-2" />
                                VIEW DETAILS
                              </Button>
                            </div>

                            {/* Right 25% - Ellipsis Button + Popover */}
                            {hasItemsAccess && (
                              <div
                                className="w-25 d-flex align-items-center justify-content-center px-2 m-2"
                                ref={popoverRef}
                              >
                                <div>
                                  <Button
                                    id={item?.id && `popover-${item.id}`}
                                    size="sm"
                                    className="p-0"
                                    color="primary"
                                    style={{
                                      width: "60px",
                                      height: "26px",
                                      color: "white",
                                    }}
                                  >
                                    <i className="fas fa-ellipsis-h" />
                                  </Button>
                                  <Popover
                                    placement="bottom-end"
                                    isOpen={popoverOpen === item.id}
                                    target={`popover-${item.id}`}
                                    toggle={() => togglePopover(item.id)}
                                  >
                                    <PopoverBody className="p-2">
                                      <div className="d-flex flex-column">
                                        {visibilityAccess && (
                                          <Button
                                            className="bg-purple text-white d-flex align-items-center w-100 m-0 mb-2 px-2"
                                            size="sm"
                                            onClick={(e) =>
                                              initiateToggleVisibility(e, item)
                                            }
                                          >
                                            <i className="fa fa-eye mr-2" />{" "}
                                            Visibility
                                          </Button>
                                        )}
                                        {editAccess && (
                                          <Button
                                            color="warning"
                                            className="d-flex align-items-center w-100 m-0 mb-2 px-2"
                                            size="sm"
                                            onClick={() =>
                                              initiateUpdateItem(item)
                                            }
                                          >
                                            <i className="fa fa-edit mr-2" />{" "}
                                            Edit
                                          </Button>
                                        )}
                                        {deleteAccess && (
                                          <Button
                                            color="danger"
                                            className="d-flex align-items-center w-100 m-0 mb-2 px-2"
                                            size="sm"
                                            onClick={() => initiateDelete(item)}
                                          >
                                            <i className="fa fa-trash mr-2" />{" "}
                                            Delete
                                          </Button>
                                        )}
                                        {duplicateAccess && (
                                          <Button
                                            color="primary"
                                            className="d-flex align-items-center m-0 w-100 mb-2 px-2"
                                            size="sm"
                                            onClick={() =>
                                              initiateDuplicate(item)
                                            }
                                          >
                                            <i className="fa fa-copy mr-2" />{" "}
                                            Duplicate
                                          </Button>
                                        )}
                                      </div>
                                    </PopoverBody>
                                  </Popover>
                                </div>
                              </div>
                            )}
                          </CardFooter>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="d-flex flex-column justify-content-center align-items-center py-5">
                    No Item found
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </Row>
      </Container>
      {itemModal && (
        <ItemModal
          isOpen={itemModal}
          toggle={itemModalToggler}
          categoryId={categoryId}
          editMode={editMode}
          viewMode={viewMode}
          activeItem={activeItem}
          menuParamId={menuParamId}
        />
      )}
      <ConfirmationModal
        title="Toggle Item Visibility"
        text={
          <>
            <h3>
              Are you sure you want to{" "}
              {!activeItem?.isVisible ? "show" : "hide"} this item?
            </h3>
            <span style={{ fontSize: "14px" }}>
              <strong>Note: </strong>
              This item will {activeItem?.isVisible && "not"} be visibile to
              customer on main site.
            </span>
          </>
        }
        isOpen={visibilityModal}
        toggle={visibilityModalToggler}
        isLoading={isLoading}
        onConfirmation={handleToggleItemVisibility}
      />
      <ConfirmationModal
        title="Duplicate Item"
        text="Are you sure you want to duplicate this item?"
        isOpen={duplicateModal}
        toggle={duplicateModalToggler}
        isLoading={isLoading}
        onConfirmation={handleDuplicateItem}
      />
      <ConfirmationModal
        title="Delete Item"
        text="Are you sure you want to delete this item?"
        isOpen={confirmationModal}
        toggle={confirmationModalToggler}
        isLoading={isLoading}
        onConfirmation={handleDeleteItem}
      />
    </>
  );
};

export default Items;
